import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Select,
  Checkbox,
  Button,
  Text,
  BlockStack,
  InlineStack,
  Banner,
  Box,
  ColorPicker,
  Spinner,
  hsbToHex,
  hexToRgb,
  rgbToHsb,
  type HSBColor,
} from '@shopify/polaris';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../lib/api';

type ButtonPosition = 'below-add-to-cart' | 'above-add-to-cart' | 'beside-add-to-cart';

interface WidgetSettings {
  widget_enabled: boolean;
  button_text: string;
  button_color: string;
  button_position: ButtonPosition;
  show_on_mobile: boolean;
  show_on_desktop: boolean;
}

interface SettingsResponse {
  shop: string;
  settings: WidgetSettings;
}

interface SaveResponse {
  success: boolean;
  settings: WidgetSettings;
}

const DEFAULTS: WidgetSettings = {
  widget_enabled: true,
  button_text: 'Try It On',
  button_color: '#000000',
  button_position: 'below-add-to-cart',
  show_on_mobile: true,
  show_on_desktop: true,
};

const positionOptions = [
  { label: 'Below Add to Cart button', value: 'below-add-to-cart' },
  { label: 'Above Add to Cart button', value: 'above-add-to-cart' },
  { label: 'Next to Add to Cart button', value: 'beside-add-to-cart' },
];

/** Polaris ColorPicker speaks HSB; the API stores a hex string. */
function hexToHsb(hex: string): HSBColor {
  const rgb = hexToRgb(hex);
  return rgbToHsb(rgb);
}

export default function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [widgetEnabled, setWidgetEnabled] = useState(DEFAULTS.widget_enabled);
  const [buttonText, setButtonText] = useState(DEFAULTS.button_text);
  const [buttonColor, setButtonColor] = useState<HSBColor>(hexToHsb(DEFAULTS.button_color));
  const [position, setPosition] = useState<string>(DEFAULTS.button_position);
  const [showOnMobile, setShowOnMobile] = useState(DEFAULTS.show_on_mobile);
  const [showOnDesktop, setShowOnDesktop] = useState(DEFAULTS.show_on_desktop);

  const applySettings = useCallback((s: WidgetSettings) => {
    setWidgetEnabled(s.widget_enabled ?? DEFAULTS.widget_enabled);
    setButtonText(s.button_text ?? DEFAULTS.button_text);
    setButtonColor(hexToHsb(s.button_color ?? DEFAULTS.button_color));
    setPosition(s.button_position ?? DEFAULTS.button_position);
    setShowOnMobile(s.show_on_mobile ?? DEFAULTS.show_on_mobile);
    setShowOnDesktop(s.show_on_desktop ?? DEFAULTS.show_on_desktop);
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiGet<SettingsResponse>('/api/settings')
      .then((data) => {
        if (cancelled) return;
        if (data?.settings) applySettings(data.settings);
      })
      .catch((err) => {
        console.error('[Settings] failed to load:', err);
        if (!cancelled) setError('Could not load your settings. Please reload the page.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applySettings]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const result = await apiPost<SaveResponse>('/api/settings', {
        widget_enabled: widgetEnabled,
        button_text: buttonText,
        button_color: hsbToHex(buttonColor),
        button_position: position as ButtonPosition,
        show_on_mobile: showOnMobile,
        show_on_desktop: showOnDesktop,
      });
      // Render what the server stored, so the page can never claim a save that
      // did not happen (the old page showed a success banner unconditionally).
      if (result?.settings) applySettings(result.settings);
      setSaved(true);
    } catch (err) {
      console.error('[Settings] save failed:', err);
      setError('Could not save your settings. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [
    applySettings,
    buttonColor,
    buttonText,
    position,
    showOnDesktop,
    showOnMobile,
    widgetEnabled,
  ]);

  const previewHex = hsbToHex(buttonColor);

  return (
    <Page
      title="Settings"
      backAction={{ content: 'Dashboard', onAction: () => navigate('/') }}
      primaryAction={{
        content: 'Save',
        onAction: handleSave,
        loading: saving,
        disabled: loading || saving,
      }}
    >
      <Layout>
        {error ? (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        ) : null}

        {saved ? (
          <Layout.Section>
            <Banner title="Settings saved" tone="success" onDismiss={() => setSaved(false)}>
              <p>Your storefront widget has been updated.</p>
            </Banner>
          </Layout.Section>
        ) : null}

        {loading ? (
          <Layout.Section>
            <Card>
              <Box padding="600">
                <InlineStack align="center">
                  <Spinner accessibilityLabel="Loading settings" size="large" />
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        ) : (
          <>
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Storefront Widget
                  </Text>
                  <FormLayout>
                    <Checkbox
                      label="Show the try-on button on my storefront"
                      checked={widgetEnabled}
                      onChange={setWidgetEnabled}
                      helpText="Turn this off to hide the button on every product without removing the app."
                    />
                  </FormLayout>
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Widget Appearance
                  </Text>
                  <FormLayout>
                    <TextField
                      label="Button Text"
                      value={buttonText}
                      onChange={setButtonText}
                      autoComplete="off"
                      maxLength={40}
                      helpText="The text displayed on the try-on button"
                    />

                    <Box>
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="p" variant="bodyMd">
                          Button Color
                        </Text>
                        <Text as="span" variant="bodySm" tone="subdued">
                          {previewHex}
                        </Text>
                      </InlineStack>
                      <Box paddingBlockStart="200">
                        <ColorPicker onChange={setButtonColor} color={buttonColor} />
                      </Box>
                      <Box paddingBlockStart="200">
                        <Button
                          variant="primary"
                          disabled
                          // Preview only — shows the merchant what buyers will see.
                        >
                          {buttonText || 'Try It On'}
                        </Button>
                      </Box>
                    </Box>

                    <Select
                      label="Button Position"
                      options={positionOptions}
                      value={position}
                      onChange={setPosition}
                      helpText="Where the try-on button appears on product pages"
                    />
                  </FormLayout>
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Display Options
                  </Text>
                  <FormLayout>
                    <Checkbox
                      label="Show on mobile devices"
                      checked={showOnMobile}
                      onChange={setShowOnMobile}
                    />
                    <Checkbox
                      label="Show on desktop"
                      checked={showOnDesktop}
                      onChange={setShowOnDesktop}
                    />
                  </FormLayout>
                </BlockStack>
              </Card>
            </Layout.Section>
          </>
        )}
      </Layout>
    </Page>
  );
}
